/**
 * ReplyCard — shared outreach reply component (Sprint 7.6 Bloque A).
 * Dumb / presentational. Hooks live in module-specific wrappers.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { Icon } from '../Icon.js';
import { MessageComposer } from '../ui/MessageComposer.js';
import { timeAgo, htmlToText } from './utils.js';
import { cuerpoVacio } from '../../lib/textoEnriquecido.js';
import type { NormalizedReply, ReplyCardLabels, MutationState } from './types.js';

interface ReplyCardProps {
  reply: NormalizedReply;
  labels: ReplyCardLabels;
  onVerify?: () => void;   // undefined = no aplica (Projects)
  onDismiss?: () => void;  // undefined = no aplica (Projects)
  onReply?: (body: { bodyHtml: string; bodyText: string; attachments?: File[] }) => void;
  replyState?: MutationState;
  allowAttachments?: boolean;
}

export function ReplyCard({
  reply,
  labels,
  onVerify,
  onDismiss,
  onReply,
  replyState,
  allowAttachments = false,
}: ReplyCardProps) {
  const { i18n } = useTranslation();
  const [showQuote, setShowQuote] = useState(false);
  // Bitácora V2: entrante sin verificar arranca abierta (pide acción);
  // el resto colapsada a una línea.
  const [open, setOpen] = useState(reply.direction !== 'outbound' && reply.isVerified !== 1);
  const [composerOpen, setComposerOpen] = useState(false);
  const [composerBody, setComposerBody] = useState('');

  const isOutbound = reply.direction === 'outbound';
  const isVerified = reply.isVerified === 1;
  const borderColor = isOutbound ? 'var(--evo-stroke)' : (isVerified ? 'var(--evo-cyan)' : 'var(--evo-amber)');
  const displayFrom = reply.fromAddress || (reply.fromDomain ? `@${reply.fromDomain}` : '—');
  const dateMs = reply.timestamp ? (typeof reply.timestamp === 'number' ? reply.timestamp : Number(reply.timestamp)) : null;
  const dateStr = dateMs
    ? new Date(dateMs).toLocaleString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : `${timeAgo(reply.receivedAt)} ago`;

  const bodyText = reply.replyText ?? reply.bodyText ?? null;
  const replyPending = replyState?.isPending ?? false;

  function handleSend(attachments?: File[]) {
    const text = htmlToText(composerBody);
    if (cuerpoVacio(composerBody)) return;
    onReply?.({
      bodyHtml: composerBody,
      bodyText: text,
      ...(allowAttachments ? { attachments: attachments ?? [] } : {}),
    });
    setComposerOpen(false);
    setComposerBody('');
  }

  return (
    <div style={{ border: `1px solid ${borderColor}`, borderRadius: 'var(--evo-r)', overflow: 'hidden' }}>
      {/* Entrada de bitácora V2: disco email (relleno tenue = entrante) ·
          dirección · asunto/remitente · hora · chevron. */}
      <div
        onClick={() => setOpen(v => !v)}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg-hover)'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--evo-log-row-bg)'; }}
        style={{
          display: 'flex', alignItems: 'center', gap: 'var(--evo-log-icon-gap)',
          minHeight: 'var(--evo-log-row-min-h)', boxSizing: 'border-box',
          padding: 'var(--evo-log-row-py) var(--evo-log-row-px)',
          background: 'var(--evo-log-row-bg)', cursor: 'pointer', transition: 'background 80ms',
        }}
      >
        <span style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box',
          width: 'var(--evo-log-icon-sz)', height: 'var(--evo-log-icon-sz)', flexShrink: 0,
          borderRadius: 'var(--evo-r-pill)', border: '1px solid var(--evo-ch-email)',
          background: isOutbound ? 'transparent' : 'color-mix(in srgb, var(--evo-ch-email) 14%, transparent)',
          color: 'var(--evo-ch-email)',
        }}>
          <Icon name="email" size={13} />
        </span>
        <span style={{
          flexShrink: 0, width: 'var(--evo-log-label-w)', fontSize: 'var(--evo-fs-caption)',
          fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap',
          overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {isOutbound ? labels.outbound : labels.inbound}
        </span>
        <span style={{
          flex: 1, minWidth: 0, fontSize: 'var(--evo-list-title-fs)', fontFamily: 'var(--evo-font)',
          color: 'var(--evo-fg-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {reply.subject || displayFrom}
        </span>
        {!isOutbound && !isVerified && onVerify && onDismiss && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            height: 'var(--evo-pill-h-xs)', padding: '0 var(--evo-pill-px)',
            fontSize: 'var(--evo-pill-fs-xs)', fontWeight: 'var(--evo-fw-medium)', lineHeight: 1,
            color: 'var(--evo-amber)', background: 'transparent',
            border: '1px solid var(--evo-amber)', borderRadius: 'var(--evo-r-pill)',
            whiteSpace: 'nowrap', boxSizing: 'border-box', fontFamily: 'var(--evo-font)',
          }}>
            {labels.possible}
          </span>
        )}
        <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', whiteSpace: 'nowrap' }}>
          {dateStr}
        </span>
        <span style={{ flexShrink: 0, display: 'inline-flex', color: 'var(--evo-fg-4)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
          <Icon name="chevron-down" size={12} />
        </span>
      </div>
      {!open ? null : isOutbound ? (
        <div style={{ padding: '8px 16px', background: 'rgba(100,100,100,0.04)', borderBottom: '1px solid var(--evo-stroke)' }}>
          <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-fg-3)', fontWeight: 'var(--evo-fw-semibold)', letterSpacing: '0.5px' }}>
            {labels.outbound}
          </span>
        </div>
      ) : (
        <>
          {!isVerified && onVerify && onDismiss && (
            <div style={{ padding: '8px 16px', background: 'rgba(251,191,36,0.08)', borderBottom: `1px solid ${borderColor}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-amber)', fontWeight: 'var(--evo-fw-semibold)' }}>
                {labels.possible}
              </span>
              <div style={{ display: 'flex', gap: '6px' }}>
                <Button variant="outline" tone="cyan" size="sm" onClick={onVerify}>{labels.confirm}</Button>
                <Button variant="ghost" tone="muted" size="sm" onClick={onDismiss}>{labels.dismiss}</Button>
              </div>
            </div>
          )}
          {(isVerified || (!onVerify && !onDismiss)) && (
            <div style={{ padding: '8px 16px', background: 'rgba(0,220,130,0.06)', borderBottom: `1px solid ${borderColor}` }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)', fontWeight: 'var(--evo-fw-semibold)' }}>
                {(!onVerify && !onDismiss) ? `${labels.inbound} — ${timeAgo(reply.receivedAt)} ago` : labels.verified}
              </span>
            </div>
          )}
        </>
      )}

      {/* Meta */}
      {open && <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {isOutbound ? (
          <>
            {reply.toAddress && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.to}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.toAddress}</span>
              </div>
            )}
            {reply.subject && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.subject}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.subject}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.sent}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{dateStr}</span>
            </div>
          </>
        ) : (
          <>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.from}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', fontWeight: 'var(--evo-fw-medium)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayFrom}</span>
            </div>
            {reply.subject && (
              <div style={{ display: 'flex', gap: '8px' }}>
                <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.subject}</span>
                <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{reply.subject}</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', width: '64px', flexShrink: 0 }}>{labels.received}</span>
              <span style={{ fontSize: 'var(--evo-fs-label)', color: 'var(--evo-fg-2)', fontFamily: 'var(--evo-font)' }}>{dateStr}</span>
            </div>
          </>
        )}
      </div>}

      {/* Reply body */}
      {open && bodyText && (
        <>
          <div style={{ height: '1px', background: 'var(--evo-stroke)', margin: '0 16px' }} />
          <div style={{ padding: '12px 16px' }}>
            <p style={{ fontSize: 'var(--evo-fs-body-lg)', color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {reply.replyText ? bodyText : bodyText.slice(0, 800)}
            </p>
            {reply.quoteText && (
              <div style={{ marginTop: '8px' }}>
                <Button variant="link" tone="muted" size="sm" onClick={() => setShowQuote(s => !s)}>
                  {showQuote ? labels.hideQuote : labels.showQuote} {showQuote ? '▴' : '▾'}
                </Button>
                {showQuote && (
                  <div style={{ marginTop: '6px', paddingLeft: '10px', borderLeft: '2px solid var(--evo-stroke)' }}>
                    <p style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-3)', fontFamily: 'var(--evo-font)', margin: 0, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      {reply.quoteText}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Actions / Composer */}
      {open && onReply && (
        <div style={{ borderTop: '1px solid var(--evo-stroke)', padding: '10px 16px' }}>
          {!composerOpen ? (
            <Button variant="outline" tone="cyan" size="sm" onClick={() => setComposerOpen(true)}>{labels.replyBtn}</Button>
          ) : (
            <MessageComposer
              value={composerBody}
              onChange={setComposerBody}
              placeholder={labels.composerPlaceholder}
              primaryLabel={labels.sendReplyBtn}
              loadingLabel={labels.sending}
              onPrimary={handleSend}
              onCancel={() => { setComposerOpen(false); setComposerBody(''); replyState?.reset?.(); }}
              loading={replyPending}
              allowAttachments={allowAttachments}
              attachLabel={labels.attachBtn}
              sizeExceededLabel={labels.sizeExceeded}
              error={replyState?.isError ? (replyState.error?.message ?? labels.actionFailed) : null}
              contextLabel={labels.replyTo.replace('{to}', displayFrom)}
            />
          )}
        </div>
      )}
    </div>
  );
}
